import React from 'react'
import { generatePath, Link, useLocation, useParams } from 'react-router-dom'
import {
  FaIcon,
  faMoneyBillTrendUp,
  faPrint,
  faListCheck,
  faCalculator,
  faArrowsRotate,
  faTrash,
  faRectangleXmark,
  faXmark,
  faMoneyBillRegular,
  faTableColumnsSolid,
} from '@/lib/fa'
import {
  faScissors,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { Checkbox } from '@/ui'
import { faClock } from '@fortawesome/pro-light-svg-icons'
import {
  faHandHoldingCircleDollar,
  faFolderArrowDown,
} from '@awesome.me/kit-2590405d52/icons/classic/solid'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { driversRoutesMap } from '../../routes-config'
import { TimesheetViewMode } from '../../tabs/registry/components'
import { usePayrollActionBar } from '../../tabs/registry/views/payroll/PayrollActionBarContext'

import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import {
  SegmentButton,
  SegmentGroup,
} from '../../tabs/registry/views/payroll/components/Segmentutton'
import {
  SVGComponent1,
  SVGComponent2,
} from '../../tabs/registry/views/payroll/icons'
import Tooltip from '../ToolTip'
import DividerVertical from '../DividerVertical'
import { useSideSelectionStore } from '../../stores/sideSelectionStore'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'

const PayrollActionBar = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { pathname } = useLocation()
  const { t } = useLocalize()
  const { busyTooltip } = usePrintJobGuard()

  const viewMode: TimesheetViewMode = pathname.includes('bonusAndRewards')
    ? 'bonus-reward'
    : 'group-view'

  const {
    toolbarState,
    onToggleBailiffDeduction,
    onToggleEquivalent,
    onToggleLeaveAdditionColumns,
    onToggleAutomaticOvertime,
    onToggleZusPaidFromAverageGross,
    onSetDomesticTripType,
    onSubmit,
    onSubmitModified,
    modifiedCount,
    onClearAll,
    onDelete,
    onPrint,
    printShortcuts,
    onRefresh,
    onImport,
    isRowsLoading,
    calculateBusy,
    calculateBusyBy,
    showOnlyNettoMismatch,
    onToggleShowOnlyNettoMismatch,
    nettoMismatchCount,
  } = usePayrollActionBar()

  // Grid rows are still streaming in for the current selection — the actions below
  // are built from grid state, so block them until the load settles.
  const blockedCls = isRowsLoading
    ? 'tw-pointer-events-none tw-opacity-40'
    : ''
  const guard = (fn: () => void) => () => {
    if (!isRowsLoading) fn()
  }

  // Osobna blokada dla samego przeliczania: siatka moze byc gotowa, a mimo to okres
  // jest juz liczony (przez nas albo przez kogos innego z firmy). Klikniecie
  // skonczyloby sie odmowa serwera, wiec lepiej powiedziec to od razu.
  const calculateBlockedCls =
    isRowsLoading || calculateBusy
      ? 'tw-pointer-events-none tw-opacity-40'
      : ''
  const guardCalculate = (fn: () => void) => () => {
    if (!isRowsLoading && !calculateBusy) fn()
  }
  const busyLabel = calculateBusyBy
    ? `${calculateBusyBy}: ${t('timesheet.calculation-in-progress')}`
    : t('timesheet.calculation-in-progress')
  const calculateLabel = isRowsLoading
    ? t('timesheet.loading-drivers')
    : calculateBusy
      ? busyLabel
      : t('timesheet.calculate')
  const calculateModifiedLabel = isRowsLoading
    ? t('timesheet.loading-drivers')
    : calculateBusy
      ? busyLabel
      : `${t('timesheet.calculate-modified')} (${modifiedCount})`

  const { sideCheckedIds } = useSideSelectionStore()
  const { basicDrivers } = useDrivers()

  const hasSelection = sideCheckedIds.length > 0

  return (
    <div className="tw-flex tw-z-[200] tw-w-full tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
      {/* Left */}
      <div className="tw-flex tw-items-center tw-gap-3">
        {/* View toggle */}
        <div className="tw-flex tw-items-center tw-gap-3">
          <Tooltip text={t('timesheet.list')}>
            <Link
              to={generatePath(driversRoutesMap.driverGroupTimesheet, {
                companyId,
              })}
            >
              <FaIcon
                icon={faListCheck}
                className={`tw-h-4 tw-w-4 ${viewMode !== 'bonus-reward' ? 'tw-text-[#2448bb]' : 'tw-text-[#6b77a0]'}`}
              />
              {viewMode === 'group-view' && (
                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-h-[3px] tw-w-[3px] -tw-translate-x-1/2 tw-translate-y-[6px] tw-rounded-[4px] tw-bg-[#2448bb]" />
              )}
            </Link>
          </Tooltip>
          <Tooltip text={t('timesheet.bonus-and-reward')} align="start">
            <Link
              to={generatePath(
                driversRoutesMap.driverGroupTimesheetBonusAndRewards,
                { companyId },
              )}
            >
              <FaIcon
                icon={faMoneyBillTrendUp}
                className={`tw-h-4 tw-w-4 ${viewMode === 'bonus-reward' ? 'tw-text-[#2448bb]' : 'tw-text-[#6b77a0]'}`}
              />
              {viewMode === 'bonus-reward' && (
                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-h-[3px] tw-w-[3px] -tw-translate-x-1/2 tw-translate-y-[6px] tw-rounded-[4px] tw-bg-[#2448bb]" />
              )}
            </Link>
          </Tooltip>
        </div>

        <DividerVertical />

        <SegmentGroup>
          <SegmentButton
            checked={toolbarState.domesticBusinessTripDateType === 'start'}
            onClick={() => onSetDomesticTripType('start')}
            tooltip={t('timesheet.domestic-delegations-starting')}
            Icon={SVGComponent1}
          />
          <SegmentButton
            checked={toolbarState.domesticBusinessTripDateType === 'end'}
            onClick={() => onSetDomesticTripType('end')}
            tooltip={t('timesheet.domestic-delegations-ending')}
            Icon={SVGComponent2}
          />
        </SegmentGroup>

        {/* Bailiff deduction */}
        <Tooltip
          text={t('timesheet.bailiff-deductions')}
          onClick={onToggleBailiffDeduction}
        >
          <div
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6] ${toolbarState.displayBailiffDeduction ? 'tw-text-primary' : 'tw-text-[#6b77a0]'}`}
          >
            <FaIcon icon={faScissors} className="tw-h-4 tw-w-4" />
          </div>
        </Tooltip>

        {/* Equivalent (finalLeavesGrossAmount) */}
        <Tooltip
          text={t('timesheet.finalLeavesGrossAmount')}
          onClick={onToggleEquivalent}
        >
          <div
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6] ${toolbarState.displayEquivalent ? 'tw-text-primary' : 'tw-text-[#6b77a0]'}`}
          >
            <FaIcon icon={faMoneyBillRegular} className="tw-h-4 tw-w-4" />
          </div>
        </Tooltip>

        <Tooltip
          text={t('timesheet.show-leave-addition-columns')}
          onClick={onToggleLeaveAdditionColumns}
        >
          <div
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6] ${toolbarState.displayLeaveAdditionColumns ? 'tw-text-primary' : 'tw-text-[#6b77a0]'}`}
          >
            <FaIcon icon={faTableColumnsSolid} className="tw-h-4 tw-w-4" />
          </div>
        </Tooltip>

        <Tooltip
          text={t('timesheet.automatic-overtime-compensation')}
          onClick={onToggleAutomaticOvertime}
        >
          <div
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6] ${toolbarState.automaticOvertimeCompensation ? 'tw-text-primary' : 'tw-text-[#6b77a0]'}`}
          >
            <FaIcon icon={faClock} className="tw-h-4 tw-w-4" />
          </div>
        </Tooltip>

        <Tooltip
          text={t('timesheet.zus-from-average-gross')}
          onClick={onToggleZusPaidFromAverageGross}
        >
          <div
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6] ${toolbarState.zusPaidFromAverageGross ? 'tw-text-primary' : 'tw-text-[#6b77a0]'}`}
          >
            <FaIcon
              icon={faHandHoldingCircleDollar}
              className="tw-h-4 tw-w-4"
            />
          </div>
        </Tooltip>

        {hasSelection && (
          <>
            <DividerVertical />

            <Tooltip text={calculateLabel} onClick={guardCalculate(onSubmit)}>
              <div
                className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] ${calculateBlockedCls}`}
              >
                <FaIcon
                  icon={isRowsLoading ? faSpinner : faCalculator}
                  className="tw-h-4 tw-w-4 tw-text-primary"
                  spin={isRowsLoading}
                />
                <span className="tw-text-sm tw-font-medium tw-text-primary">
                  {calculateLabel}
                </span>
              </div>
            </Tooltip>

            {modifiedCount > 0 && (
              <Tooltip
                text={calculateModifiedLabel}
                onClick={guardCalculate(onSubmitModified)}
              >
                <div
                  className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] ${calculateBlockedCls}`}
                >
                  <FaIcon
                    icon={isRowsLoading ? faSpinner : faCalculator}
                    className="tw-h-4 tw-w-4 tw-text-[#f59e0b]"
                    spin={isRowsLoading}
                  />
                  <span className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-[#f59e0b]">
                    {calculateModifiedLabel}
                  </span>
                </div>
              </Tooltip>
            )}

            {(nettoMismatchCount > 0 || showOnlyNettoMismatch) && (
              <Tooltip
                text={t('timesheet.netto-mismatch-filter-tooltip')}
                portal
                maxWidth="320px"
                onClick={
                  showOnlyNettoMismatch
                    ? onToggleShowOnlyNettoMismatch
                    : guard(onToggleShowOnlyNettoMismatch)
                }
              >
                <div
                  className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] ${showOnlyNettoMismatch ? 'tw-bg-[#e5e6e6]' : blockedCls}`}
                >
                  <Checkbox
                    checked={showOnlyNettoMismatch}
                    readOnly
                    tabIndex={-1}
                    aria-hidden
                  />
                  <FaIcon
                    icon={faTriangleExclamation}
                    className="tw-h-4 tw-w-4 tw-text-amber-600"
                  />
                  <span className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-amber-600">
                    {`${t('timesheet.netto-mismatch-filter')} (${nettoMismatchCount})`}
                  </span>
                </div>
              </Tooltip>
            )}
          </>
        )}
      </div>

      <div className="tw-flex tw-items-center tw-gap-2">
        {hasSelection && (
          <>
            <Tooltip
              text={t('timesheet.delete-timesheet')}
              onClick={guard(onDelete)}
            >
              <div
                className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] ${blockedCls}`}
              >
                <FaIcon
                  icon={faTrash}
                  className="tw-h-4 tw-w-4 tw-text-red-500"
                />
                <span className="tw-text-sm tw-font-medium tw-text-red-500">
                  {t('timesheet.delete-timesheets')}
                </span>
              </div>
            </Tooltip>
            <Tooltip
              text={t('common.print')}
              onClick={guard(onPrint)}
              blockedText={busyTooltip}
            >
              <div
                className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] ${blockedCls}`}
              >
                <FaIcon
                  icon={faPrint}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
                <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                  {t('common.print')}
                </span>
              </div>
            </Tooltip>
            {printShortcuts}
            <DividerVertical />
          </>
        )}

        <Tooltip text={t('timesheet.import')} onClick={onImport}>
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faFolderArrowDown}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>

        <Tooltip
          text={t('timesheet.clear-modified-values')}
          onClick={onClearAll}
        >
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faRectangleXmark}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>

        <DividerVertical />

        <Tooltip text={t('timesheet.refresh')} onClick={onRefresh}>
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faArrowsRotate}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>

      
      </div>
    </div>
  )
}

export default PayrollActionBar
